/**
 * Help Centre > Guide videos.
 *
 * Each entry is a placeholder until a link is pasted into `url`. Paste the
 * ordinary share link (YouTube, Vimeo or Loom); `toEmbedUrl` turns it into an
 * embeddable player. Any other host is opened in a new tab instead. A `null`
 * url renders the card as "Coming soon", so the list can ship before the
 * recordings exist.
 *
 * `heading` groups a run of entries under a visible sub-heading (e.g. "How to
 * get started"): consecutive entries whose `heading` is the same string
 * render as one grid under one heading. Set it on EVERY entry in the group,
 * not just the first — inserting or reordering a video later shouldn't
 * depend on remembering which earlier entry it implicitly inherits from.
 * Leave `heading` unset entirely for a video that stands alone with no
 * heading above it.
 */
export interface GuideVideo {
  id: string
  title: string
  description: string
  /** Share link, or null while the video is still being produced. */
  url: string | null
  /** Shown on the card when known, e.g. "4 min" or "1:49". */
  duration?: string
  /** Sub-heading this entry's group renders under — set on every member. */
  heading?: string
}

export const GUIDE_VIDEOS: GuideVideo[] = [
  {
    id: 'product-overview',
    title: 'Product overview',
    description: 'A tour of Beevelope end to end: contacts, workflows, campaigns, the inbox and analytics.',
    // "Beevelope — AI outreach that researches every prospect first"
    url: 'https://youtu.be/-D_5-7g1MY4',
    duration: '2:43',
    heading: 'Product Video',
  },
  // "How to get started" — the "Beevelope How to Videos" YouTube playlist
  // (https://www.youtube.com/playlist?list=PLQz5Hv76qVAQ), in playlist order.
  // Titles are trimmed of the playlist's own "Beevelope | ... | Walkthrough"
  // framing; durations are copied from each video's own badge.
  {
    id: 'guide-warmup-account',
    title: 'Warm up a sending account',
    description: 'Connect a provider and ramp a mailbox before it sends cold outreach.',
    url: 'https://www.youtube.com/watch?v=0ndZ5hzPHoM',
    duration: '1:49',
    heading: 'How to get started',
  },
  {
    id: 'guide-view-analytics',
    title: 'View analytics',
    description: 'Read deliverability, opens, replies and campaign performance.',
    url: 'https://www.youtube.com/watch?v=iFB5NY-lS00',
    duration: '1:33',
    heading: 'How to get started',
  },
  {
    id: 'guide-connect-communication',
    title: 'Connect communication',
    description: 'Link the channels a workflow can message a contact through.',
    url: 'https://www.youtube.com/watch?v=teavu8vP50s',
    duration: '1:06',
    heading: 'How to get started',
  },
  {
    id: 'guide-add-contacts',
    title: 'Add contacts',
    description: 'Add one by hand, import a spreadsheet, or pull prospects from Apollo.',
    url: 'https://www.youtube.com/watch?v=lN79uvyeV3s',
    duration: '2:27',
    heading: 'How to get started',
  },
  {
    id: 'guide-connect-enrichment',
    title: 'Connect enrichment',
    description: 'Hook up a provider that fills in missing contact and company detail.',
    url: 'https://www.youtube.com/watch?v=u9XThz6aazQ',
    duration: '1:02',
    heading: 'How to get started',
  },
  {
    id: 'guide-create-templates',
    title: 'Create templates',
    description: 'Give the AI your collateral and a structure to write from.',
    url: 'https://www.youtube.com/watch?v=qelLRNhCACs',
    duration: '2:32',
    heading: 'How to get started',
  },
  {
    id: 'guide-create-workflows',
    title: 'Create workflows',
    description: 'Drag nodes onto the canvas, connect them, and set the workflow to Active.',
    url: 'https://www.youtube.com/watch?v=efG7ruwL5Ho',
    duration: '2:30',
    heading: 'How to get started',
  },
  {
    id: 'guide-connect-calendar',
    title: 'Connect a calendar',
    description: 'Let a workflow check availability and book meetings.',
    url: 'https://www.youtube.com/watch?v=wUo8tAmjidU',
    duration: '0:58',
    heading: 'How to get started',
  },
  {
    id: 'guide-add-materials',
    title: 'Add materials',
    description: 'Upload the collateral a campaign can draw on while it writes.',
    url: 'https://www.youtube.com/watch?v=ADO_6qYzbFk',
    duration: '1:11',
    heading: 'How to get started',
  },
  {
    id: 'guide-connect-crm',
    title: 'Connect your CRM',
    description: 'Sync contacts and activity with the CRM your team already uses.',
    url: 'https://www.youtube.com/watch?v=Jf_K_gThDX8',
    duration: '1:18',
    heading: 'How to get started',
  },
  {
    id: 'guide-run-campaigns',
    title: 'Run campaigns',
    description: 'Pick the workflow, the sender and the audience, then schedule or send now.',
    url: 'https://www.youtube.com/watch?v=EDsx4wWp0rc',
    duration: '1:39',
    heading: 'How to get started',
  },
]
